import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
} from "@mui/material"
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline"

/**
 * Mapea mensajes técnicos de AFIP/SDK a mensajes entendibles para el usuario.
 */
const MAPEO_ERRORES_AFIP = [
  {
    patron: /10016|número o fecha del comprobante|próximo a autorizar/i,
    mensaje:
      "El número o fecha del comprobante no coincide con el que AFIP espera. Verificá que el punto de venta esté correcto y que la fecha sea igual o posterior a la última factura emitida.",
  },
  {
    patron: /11002|punto de venta.*no.*habilitado|no se encuentra habilitado/i,
    mensaje:
      "El punto de venta no está habilitado para facturar electrónicamente. Creá o habilitá el punto de venta en la página de AFIP/ARCA.",
  },
  {
    patron: /DocNro|documento.*inválido|documento.*receptor/i,
    mensaje:
      "El documento del cliente (DNI/CUIT/CUIL) es inválido o no corresponde al tipo de factura. Para consumidor final usá DNI o 0. Para facturas A/B el cliente debe tener CUIT/CUIL válido.",
  },
  {
    patron: /certificado|Certificado no emitido|AC de confianza/i,
    mensaje:
      "Hay un problema con los certificados de AFIP. Verificá que estén configurados correctamente para el ambiente (prueba o producción) que estás usando.",
  },
  {
    patron: /openssl|pkcs7|BEGIN CERTIFICATE/i,
    mensaje:
      "Error en el certificado digital. Asegurate de usar el certificado correcto (no el CRS) y que esté bien configurado en los parámetros del sistema.",
  },
  {
    patron: /600|ValidacionDeToken|verificar hash|No validó la firma/i,
    mensaje:
      "Error de autenticación con AFIP. Probá forzar una nueva autorización o verificá que el certificado corresponda al ambiente correcto.",
  },
  {
    patron: /600|No aparecio CUIT|CUIT no vinculado/i,
    mensaje:
      "El CUIT no está vinculado al certificado. Si delegaste el servicio, aceptá la delegación en AFIP y autorizá el web service.",
  },
  {
    patron: /notAuthorized|Computador no autorizado|no autorizado a acceder/i,
    mensaje:
      "El certificado no está autorizado para acceder al servicio de facturación. Autorizá el web service en la página de AFIP/ARCA.",
  },
  {
    patron: /501|Error interno de base de datos/i,
    mensaje:
      "Error interno de AFIP. No es un problema de tu sistema. Esperá unos minutos e intentá nuevamente.",
  },
  {
    patron: /alreadyAuthenticated|TA valido/i,
    mensaje:
      "AFIP ya tiene una sesión activa. Esperá 2 minutos (producción) o 10 minutos (desarrollo) antes de intentar de nuevo.",
  },
  {
    patron: /CondicionIVA|condición.*IVA|IVA.*receptor/i,
    mensaje:
      "La condición de IVA del cliente no es correcta para este tipo de factura. Verificá los datos del cliente.",
  },
  {
    patron: /ImpTotal|importe|ImpNeto|ImpIVA/i,
    mensaje:
      "Los importes de la factura no son válidos. Revisá que los totales, neto e IVA coincidan correctamente.",
  },
]

/**
 * Ejemplos orientativos: qué puede “faltar” o ser inválido cuando AFIP/servidor rechaza.
 * (No es una lista cerrada; el mensaje técnico arriba indica el caso real.)
 */
const TEXTO_EJEMPLOS_DATOS_INVALIDOS =
  "Ejemplo: emitís Factura A y el CUIT del cliente tiene menos de 11 dígitos o está mal cargado — " +
  "esos datos no son válidos para ese tipo de comprobante y AFIP rechaza la emisión.\n\n" +
  "Otros casos frecuentes: documento (DNI/CUIT) que no corresponde al tipo de factura (A/B/C); " +
  "condición de IVA del cliente incompatible; punto de venta no habilitado o desfasado; " +
  "importes donde neto + IVA no coinciden con el total; fecha anterior al último comprobante autorizado."

function traducirErrorAfip(mensajeTecnico) {
  if (!mensajeTecnico || typeof mensajeTecnico !== "string") {
    return (
      "No pudimos emitir la factura electrónica. No llegó un detalle del error desde el sistema " +
      "(a veces falta el mensaje cuando hay un corte de red o un fallo en el servidor).\n\n" +
      TEXTO_EJEMPLOS_DATOS_INVALIDOS
    )
  }
  const texto = String(mensajeTecnico).trim()
  for (const { patron, mensaje } of MAPEO_ERRORES_AFIP) {
    if (patron.test(texto)) {
      return mensaje
    }
  }
  // Sin regla específica: contexto + detalle que devolvió AFIP/backend + ejemplos para el usuario
  const detalle =
    texto.length > 280 ? `${texto.slice(0, 277)}…` : texto
  return (
    `No pudimos emitir la factura electrónica. Motivo reportado: ${detalle}\n\n` +
    TEXTO_EJEMPLOS_DATOS_INVALIDOS
  )
}

export { traducirErrorAfip }

export default function ModalErrorFacturacion({ open, onClose, errorTecnico }) {
  const mensajeAmigable = traducirErrorAfip(errorTecnico)
  const raw = errorTecnico != null ? String(errorTecnico).trim() : ""

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          color: "error.main",
          fontWeight: 700,
        }}
      >
        <ErrorOutlineIcon sx={{ fontSize: 28 }} />
        Error al facturar
      </DialogTitle>
      <DialogContent>
        <Box sx={{ py: 1 }}>
          <Typography variant="body1" sx={{ mb: 2, whiteSpace: "pre-line" }}>
            {mensajeAmigable}
          </Typography>
          {raw.length > 0 ? (
            <Typography
              variant="caption"
              sx={{
                display: "block",
                color: "text.secondary",
                fontFamily: "monospace",
                bgcolor: "grey.100",
                p: 1.5,
                borderRadius: 1,
                wordBreak: "break-word",
              }}
            >
              Detalle técnico: {errorTecnico}
            </Typography>
          ) : null}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} variant="contained" color="primary">
          Entendido
        </Button>
      </DialogActions>
    </Dialog>
  )
}
